import { css } from '@emotion/react'
import { Icon, IconImage, Theme, useTheme } from 'bold-ui'
import { HTMLAttributes } from 'react'

import { MAP_MARKER_SIZE } from './map-model'

interface MapMarkerProps extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
  icon: IconImage
  color?: string
  label?: string
}

const [PINO_WIDTH, PINO_HEIGHT] = MAP_MARKER_SIZE
const OUTLINE_WIDTH = 2

export function MapMarker(props: Readonly<MapMarkerProps>) {
  const { icon, color, label, onClick, ...rest } = props

  const theme = useTheme()
  const styles = createStyles(theme, color, !!onClick)

  const content = (
    <>
      <svg
        css={styles.shape}
        viewBox={`0 0 ${PINO_WIDTH} ${PINO_HEIGHT}`}
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M14 32C6.0005 24.419 2 18.419 2 14C2 10.8174 3.26428 7.7652 5.5147 5.51472C7.7652 3.26428 10.8174 2 14 2C17.1826 2 20.2348 3.26428 22.4853 5.51472C24.7357 7.7652 26 10.8174 26 14C26 18.419 21.9995 24.419 14 32Z'
          fill={theme.pallete.surface.main}
        />
        <path
          className='map-marker-focus-outline'
          d='M14 0C17.713 0 21.2739 1.47508 23.8994 4.10059C26.5249 6.7261 28 10.287 28 14C28 16.7822 26.7576 19.7836 24.6641 22.9238C22.5496 26.0954 19.4337 29.6067 15.376 33.4521C14.6046 34.1831 13.3954 34.1831 12.624 33.4521C8.5663 29.6067 5.4504 26.0954 3.33594 22.9238C1.24236 19.7836 0 16.7822 0 14C0 10.287 1.47508 6.7261 4.10059 4.10059C6.7261 1.47508 10.287 0 14 0ZM14 2C10.8174 2 7.7651 3.26421 5.5146 5.51465C3.26421 7.7651 2 10.8174 2 14C2 18.419 6.0005 24.419 14 32C21.9995 24.419 26 18.419 26 14C26 10.8174 24.7358 7.7651 22.4854 5.51465C20.2349 3.26421 17.1826 2 14 2Z'
          fill={theme.pallete.primary.main}
          css={styles.focusOutline}
        />
      </svg>
      <div css={styles.circle}>
        <Icon icon={icon} size={0.75} style={styles.icon} />
      </div>
    </>
  )

  if (onClick) {
    return (
      <button type='button' css={styles.root} {...rest} onClick={onClick} aria-label={label}>
        {content}
      </button>
    )
  }

  return (
    <div css={styles.root} {...rest}>
      {content}
    </div>
  )
}

const createStyles = (theme: Theme, color: string | undefined, isClickable: boolean) => ({
  root: css`
    position: relative;
    display: inline-block;
    width: ${PINO_WIDTH}px;
    height: ${PINO_HEIGHT}px;
    filter: drop-shadow(0px 1.33px 6.67px rgba(0, 0, 0, 0.12)) drop-shadow(0px 2.67px 1.33px rgba(0, 0, 0, 0.04));
    cursor: ${isClickable ? 'pointer' : 'inherit'};
    border: none;
    margin: 0;
    padding: 0;
    background: none;
    font: inherit;
    color: inherit;
    text-align: inherit;
    outline: 0;

    &:focus .map-marker-focus-outline {
      opacity: 1;
    }
  `,
  shape: css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  `,
  circle: css`
    position: absolute;
    top: ${2 + OUTLINE_WIDTH}px;
    left: ${2 + OUTLINE_WIDTH}px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${color ?? theme.pallete.primary.c60};
    z-index: ${theme.zIndex.popper};
  `,
  icon: css`
    fill: ${theme.pallete.surface.main};
  `,
  focusOutline: css`
    opacity: 0;
    transition: opacity 0.125s;
  `,
})
